import { useLayoutEffect, useRef } from 'react'
import { Link } from 'react-router-dom'

import { HealthIcon, type HealthAccent } from './HealthIcon'

interface AddDataDialogProps {
  open: boolean
  onClose(): void
}

const choices = [
  { title: '体重', to: '/metrics/weight/new', accent: 'weight' },
  { title: '体脂', to: '/metrics/bodyFat/new', accent: 'bodyFat' },
  { title: '睡眠', to: '/sleep/new', accent: 'sleep' },
  { title: '活动卡路里', to: '/metrics/activeEnergy/new', accent: 'energy' },
] as const satisfies ReadonlyArray<{ title: string; to: string; accent: HealthAccent }>

function supportsNativeModalDialog(): boolean {
  return (
    typeof HTMLDialogElement !== 'undefined' &&
    typeof HTMLDialogElement.prototype.showModal === 'function' &&
    typeof HTMLDialogElement.prototype.close === 'function'
  )
}

function focusableElements(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )].filter((element) => !element.hasAttribute('hidden'))
}

export function AddDataDialog({ open, onClose }: AddDataDialogProps) {
  const nativeDialog = supportsNativeModalDialog()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const fallbackRef = useRef<HTMLElement>(null)
  const firstChoiceRef = useRef<HTMLAnchorElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const closeHandlerRef = useRef(onClose)

  useLayoutEffect(() => {
    closeHandlerRef.current = onClose
  }, [onClose])

  useLayoutEffect(() => {
    if (!open) return
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    const nativeElement = dialogRef.current
    if (nativeDialog && nativeElement !== null && !nativeElement.open) {
      try {
        nativeElement.showModal()
      } catch {
        nativeElement.setAttribute('open', '')
      }
    }
    firstChoiceRef.current?.focus()

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeHandlerRef.current()
        return
      }
      if (event.key !== 'Tab') return

      const container = nativeDialog ? dialogRef.current : fallbackRef.current
      if (container === null) return
      const focusable = focusableElements(container)
      const first = focusable[0]
      const last = focusable.at(-1)
      if (first === undefined || last === undefined) return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      } else if (!container.contains(document.activeElement)) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (nativeElement !== null && nativeElement.open) {
        try {
          nativeElement.close()
        } catch {
          nativeElement.removeAttribute('open')
        }
      }
      previousFocusRef.current?.focus()
      previousFocusRef.current = null
    }
  }, [nativeDialog, open])

  if (!open) return null

  const content = (
    <>
      <h2 id="add-data-title">添加今日数据</h2>
      <nav aria-label="添加数据类型">
        {choices.map((choice, index) => (
          <Link
            className={`add-data-dialog__choice add-data-dialog__choice--${choice.accent}`}
            data-return-to="/"
            key={choice.to}
            ref={index === 0 ? firstChoiceRef : undefined}
            state={{ returnTo: '/' }}
            to={choice.to}
            aria-label={`记录${choice.title}`}
          >
            <HealthIcon type={choice.accent} />
            <span>{choice.title}</span>
          </Link>
        ))}
      </nav>
      <button type="button" onClick={onClose}>取消</button>
    </>
  )

  if (nativeDialog) {
    return (
      <dialog
        ref={dialogRef}
        className="add-data-dialog"
        aria-labelledby="add-data-title"
        aria-modal="true"
        onCancel={(event) => {
          event.preventDefault()
          onClose()
        }}
      >
        {content}
      </dialog>
    )
  }

  return (
    <div className="dialog-backdrop">
      <section
        ref={fallbackRef}
        className="add-data-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-data-title"
      >
        {content}
      </section>
    </div>
  )
}
